import { useMessagePlayer } from '../contexts/MessagePlayerContext';
import { LoanOwedBadge } from '../contexts/LoanOwedContext';

/**
 * Renders a player name as a click-to-message link. Looks like normal text
 * with a subtle dotted underline; on hover the underline turns gold and a
 * pointer cursor appears. Any falsy user is rendered as plain text so it
 * never breaks layout when data is partial.
 *
 * Pass `user` with at least `{ id, name }` — extra fields are forwarded to
 * the message dialog so the recipient label is friendly.
 *
 * Pass `hideOwedBadge` to suppress the "owes $X" badge (e.g. in places
 * where the outstanding amount is already shown inline).
 */
const PlayerNameLink = ({
    user,
    name,                 // fallback when only a string is available
    id,                   // optional id-only mode (looked up via the dialog when opened)
    testId,
    className = '',
    children,
    hideOwedBadge = false,
}) => {
    const { open } = useMessagePlayer();
    const resolvedUser = user || (id ? { id, name: name || '' } : null);
    const display = children || user?.name || name || '';

    if (!resolvedUser?.id || !display) {
        return <span className={className}>{display || '—'}</span>;
    }

    return (
        <span className="inline-flex items-center gap-1.5">
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); open(resolvedUser); }}
                className={`text-left underline decoration-dotted decoration-[#A0AAB2]/40 underline-offset-2 hover:text-[#FFD700] hover:decoration-[#FFD700] transition-colors ${className}`}
                title="Click to send a message"
                data-testid={testId}
            >
                {display}
            </button>
            {!hideOwedBadge && <LoanOwedBadge userId={resolvedUser.id} />}
        </span>
    );
};

export default PlayerNameLink;
